// Form schema resolver (docs/roadmap.md W5c).
//
// Loads published form_schemas (system templates + the org's own rows, which
// shadow the system ones) into an in-memory map + localStorage cache, so a
// submission can stamp its schema_id/schema_version and so an offline boot can
// still resolve the schema it last saw. Since Fase B, an ORG row is also
// authoritative for rendering: fieldsFor()/metaFor() (js/form-configs.js) read
// structure and widget content from it, with the in-code constants as the
// fallback for system rows / no row / a cold offline boot.

const CACHE_KEY = 'ordinex_schemas';
// form_key → { system, neutral, byPack: Map<pack_key, row> } — each slot
// guarda a maior versão publicada (ascending sort + overwrite).
let _entries = new Map();

// Same slug rule the seed uses (scripts/seed-form-schemas.mjs).
export function formKeyFor(submissionType) {
  return String(submissionType || '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function hydrateFrom(rows) {
  const m = new Map();
  const slot = (k) => {
    if (!m.has(k)) m.set(k, { system: null, neutral: null, byPack: new Map() });
    return m.get(k);
  };
  for (const r of [...rows].sort((a, b) => a.version - b.version)) {
    const e = slot(r.form_key);
    if (r.org_id === null) e.system = r;
    else if (r.pack_key) e.byPack.set(r.pack_key, r);
    else e.neutral = r;
  }
  _entries = m;
}

export async function loadFormSchemas(sb) {
  // Warm from cache first so resolve() works immediately (and offline).
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (Array.isArray(cached)) hydrateFrom(cached);
  } catch { /* ignore corrupt cache */ }

  // Revalidate in the background (stale-while-revalidate).
  try {
    const { data, error } = await sb.from('form_schemas')
      .select('id, org_id, form_key, pack_key, version, title, schema, status')
      .eq('status', 'published');
    if (!error && Array.isArray(data)) {
      hydrateFrom(data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* quota */ }
    }
  } catch { /* offline — keep the cached map */ }

  return _entries;
}

// Resolve por form_key ou título legado, no contexto de um trade.
// Precedência (spec §5.3): variante org do pack → org neutra → system.
export function resolveSchema(keyOrType, packKey = null) {
  const e = _entries.get(keyOrType) || _entries.get(formKeyFor(keyOrType));
  if (!e) return null;
  if (packKey && e.byPack.has(packKey)) return e.byPack.get(packKey);
  return e.neutral || e.system || null;
}

// Org-created custom forms (Fase B FB3): published org rows flagged
// meta.custom. Feeds the field app's type picker and dashboard tiles.
export function customForms() {
  const out = [];
  for (const e of _entries.values()) {
    for (const r of [e.neutral, ...e.byPack.values()]) {
      if (r && r.schema?.meta?.custom === true) {
        out.push({ id: r.title, icon: r.schema.meta.icon || '📄',
                   desc: r.schema.meta.description || '', custom: true });
      }
    }
  }
  return out.sort((a, b) => a.id.localeCompare(b.id));
}
